import React, { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react'

import { Header, Loader, ErrorHandler, CustomBabi } from 'components'
import { Page, PageContent, Divider, makeTraceDetails } from 'common'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { isEmpty } from 'lodash'
import { ScrollRestoration, useNavigate } from 'react-router-dom'
import { Routes } from 'common/constants'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { Button } from 'common/styles/layouts'

import { Input } from '@interco/inter-ui/components/Input'
import { Text } from '@interco/inter-ui/components/Text'
import IcSearch from '@interco/icons/core/action-navigation/ic_search'
import IcChevronDown from '@interco/icons/core/action-navigation/ic_chevron_down'
import IcBookOpen from '@interco/icons/core/home-objects/ic_book_open'
import IcChevronRight from '@interco/icons/core/action-navigation/ic_chevron_right'

import { FaqContent } from './components'
import { useGetFAQs } from './hooks'
import { BottomCard, PrivacyPolicyPageLink, QuestionsSection, ShowMoreButton } from './styles'
import { GetFAQsResponse } from './types/FrequentQuestions.types'

const setIsShowMoreDisabled = (
  isFaqsLoading: boolean,
  isTyping: boolean,
  faqs: GetFAQsResponse | undefined,
) => {
  const isFaqsHitLengthLessThan7 = faqs?.hits?.hit?.length ? faqs.hits.hit.length <= 7 : true

  return isFaqsLoading || isTyping || isFaqsHitLengthLessThan7
}

export const FrequentQuestions: FC = () => {
  const { userLanguage, privacyCenterContent, isAppLoading, isAppError, userSelectedEntity } =
    useAppContext()

  const [isTyping, setIsTyping] = useState(false)
  const [queryText, setQueryText] = useState('')
  const [showMore, setShowMore] = useState(false)
  const navigate = useNavigate()

  const { faqs, isFaqsLoading, isFaqsError, fetchFaqs } = useGetFAQs({
    queryText,
    language: userLanguage ?? 'pt-BR',
    userSelectedEntity: userSelectedEntity ?? 'BR',
    fetchAutomatically: queryText.length === 0,
  })

  const faqsArray = showMore ? faqs?.hits.hit : faqs?.hits.hit.slice(0, 7)

  const timerRef = useRef<null | number>(null)

  const stopTimer = useCallback(() => {
    if (!timerRef.current) return
    window.clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  const startTimer = useCallback(() => {
    if (timerRef.current) stopTimer()
    setIsTyping(true)

    timerRef.current = window.setTimeout(() => {
      fetchFaqs()
      stopTimer()
      setIsTyping(false)
    }, 1000)
  }, [fetchFaqs, stopTimer])

  useEffect(() => {
    if (!queryText) {
      stopTimer()
      setIsTyping(false)
      return undefined
    }

    startTimer()
    return stopTimer
  }, [queryText, startTimer, stopTimer])

  useEffect(() => () => stopTimer(), [stopTimer])

  const content = privacyCenterContent?.components?.faq?.content

  const isErrorOrInactive =
    isAppError ||
    isFaqsError ||
    isEmpty(content) ||
    !privacyCenterContent?.components?.faq?.isActive

  useScreenFinishLoadTac({
    name: 'FrequentQuestions',
    screen: Routes.FREQUENT_QUESTIONS,
    action: 'FrequentQuestionsFinishLoad',
    entity: userSelectedEntity,
    shouldSend: !isAppLoading && !isErrorOrInactive,
    logLabel: 'send frequent questions finish tac',
  })

  const isShowMoreDisabled = setIsShowMoreDisabled(isFaqsLoading, isTyping, faqs)

  const onSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setQueryText(e.target.value),
    [],
  )

  const onShowMoreClick = useCallback(() => {
    setShowMore((current) => !current)
  }, [setShowMore])

  if (isAppLoading) return <Loader />

  if (isErrorOrInactive) {
    const traceDetails = makeTraceDetails(
      !!privacyCenterContent?.components?.home?.isActive,
      content,
    )

    return <ErrorHandler traceDetails={traceDetails} componentName={FrequentQuestions.name} />
  }

  const { pageTitle, searchButtonPlaceholder, showMoreButton, noResultsFound, footer } = content

  const showMoreButtonText = showMore ? showMoreButton.showLess : showMoreButton.showMore

  return (
    <Page>
      <Header title={pageTitle} />
      <PageContent>
        <Input
          id="simple"
          color="var(--typography500)"
          style={{ marginBottom: '0.5rem', paddingBottom: '0' }}
          placeholder={searchButtonPlaceholder}
          iconLeft={<IcSearch width={18} height={18} color="var(--secondary500)" />}
          onChange={onSearchInputChange}
        />
        <QuestionsSection>
          <FaqContent
            isFaqLoading={isFaqsLoading}
            showMore={showMore}
            queryText={queryText}
            isTyping={isTyping}
            noResultsFound={noResultsFound}
            faqs={faqsArray}
          />
          <ShowMoreButton
            variant="link"
            disabled={isShowMoreDisabled}
            icon={
              <IcChevronDown
                height={18}
                width={18}
                className={showMore ? 'up' : 'down'}
                color={isShowMoreDisabled ? 'var(--gray300)' : 'var(--primary500)'}
              />
            }
            onClick={onShowMoreClick}
          >
            {showMoreButtonText}
          </ShowMoreButton>
        </QuestionsSection>
      </PageContent>
      <Divider />
      {!!footer && !!footer.isActive && (
        <BottomCard>
          <Text variant="headline-h3" color="typography" semiBold>
            {footer.content.title}
          </Text>
          <PrivacyPolicyPageLink
            to={Routes.PRIVACY_POLICY}
            state={{ from: Routes.FREQUENT_QUESTIONS }}
          >
            <>
              <IcBookOpen height={18} width={18} color="var(--typography500)" />
              <Text variant="caption-1" bold>
                {footer.content.message}
              </Text>
              <IcChevronRight height={22} width={22} color="var(--secondary500)" />
            </>
          </PrivacyPolicyPageLink>
          <Button
            variant="secondary"
            style={{ marginTop: '0.5rem', marginBottom: '1rem' }}
            onClick={() => {
              navigate(footer.content.to)
            }}
          >
            {footer.content.button}
          </Button>
          {footer.helpCenter && <CustomBabi />}
        </BottomCard>
      )}
      <ScrollRestoration />
    </Page>
  )
}
